import React, { useRef, useEffect } from "react";
import { Group, Circle, Image } from "react-konva";
import Konva from "konva";
import { KonvaTooltip } from "frontend/ui-components/design-system/tooltip/konva-tooltip";
import { DataLayerErrorType } from "./hooks/use-missing-data-badge";

const MISSING_DATA_ICON_SVG = `
<svg width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M7.16724 4.72266V7.38932" stroke="#F4AD29" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M6.075 1.11548L0.670998 10.1381C0.559595 10.3311 0.500643 10.5498 0.500005 10.7726C0.499367 10.9954 0.557066 11.2144 0.667362 11.408C0.777658 11.6015 0.936708 11.7628 1.12869 11.8758C1.32068 11.9889 1.5389 12.0496 1.76166 12.0521H12.571C12.7937 12.0496 13.0118 11.9888 13.2037 11.8758C13.3956 11.7628 13.5545 11.6016 13.6648 11.4081C13.7751 11.2147 13.8328 10.9957 13.8323 10.7731C13.8317 10.5504 13.7729 10.3317 13.6617 10.1388L8.25767 1.11481C8.14397 0.92715 7.9838 0.771976 7.79263 0.664278C7.60146 0.556581 7.38575 0.5 7.16633 0.5C6.94691 0.5 6.7312 0.556581 6.54003 0.664278C6.34886 0.771976 6.18869 0.92715 6.075 1.11481V1.11548Z" stroke="#F4AD29" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M7.16724 9.38965H7.17383" stroke="#F4AD29" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;

const GUEST_ICON_SVG = `
<svg width="13" height="12" viewBox="0 0 13 12" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M5.73981 1.7455C7.13738 1.57894 8.55105 1.87438 9.76495 2.58669C10.9788 3.29899 11.9263 4.38902 12.4625 5.69032C12.5125 5.82502 12.5125 5.9732 12.4625 6.1079C12.242 6.64247 11.9506 7.14496 11.5961 7.60183" stroke="#7F869B" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M7.7503 7.1945C7.41083 7.52237 6.95616 7.70379 6.48423 7.69969C6.0123 7.69559 5.56086 7.5063 5.22714 7.17258C4.89342 6.83886 4.70412 6.38741 4.70002 5.91548C4.69592 5.44355 4.87734 4.98888 5.20521 4.64941" stroke="#7F869B" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M9.78729 9.1982C8.99142 9.66964 8.10344 9.96438 7.18362 10.0624C6.2638 10.1604 5.33366 10.0594 4.45631 9.7663C3.57895 9.47317 2.77492 8.99474 2.09876 8.36349C1.4226 7.73223 0.890138 6.96291 0.537502 6.10774C0.487499 5.97303 0.487499 5.82486 0.537502 5.69016C1.06946 4.40013 2.00544 3.31741 3.20498 2.60449" stroke="#7F869B" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M1.10028 0.5L11.8995 11.2992" stroke="#7F869B" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;

const MISSING_DATA_ICON_DATA_URL = `data:image/svg+xml,${encodeURIComponent(MISSING_DATA_ICON_SVG)}`;
const GUEST_ICON_DATA_URL = `data:image/svg+xml,${encodeURIComponent(GUEST_ICON_SVG)}`;

const BADGES = {
  [DataLayerErrorType.MISSING_DATA]: {
    icon: MISSING_DATA_ICON_DATA_URL,
    tooltip: "Missing data",
  },
  [DataLayerErrorType.GUEST]: {
    icon: GUEST_ICON_DATA_URL,
    tooltip: "Unavailable to guests",
  },
};

interface DataLayerBadgeProps {
  errorType: DataLayerErrorType;
  x?: number;
  y?: number;
}

export function DataLayerBadge({ errorType, x = 0, y = 0 }: DataLayerBadgeProps) {
  const padding = 10;
  const iconImageRef = useRef<HTMLImageElement | null>(null);
  const badgeRef = useRef<Konva.Circle | null>(null);

  const badge = BADGES[errorType];

  // Positioned outside the offset Group, so coordinates are relative to element bounds
  const badgeX = x - padding;
  const badgeY = y - padding;

  useEffect(() => {
    if (typeof window !== "undefined" && !iconImageRef.current && errorType !== null) {
      const image = new window.Image();
      image.src = badge.icon;
      iconImageRef.current = image;
    }
  }, [badge.icon, errorType]);

  if (errorType === null) {
    return null;
  }

  return (
    <>
      <Group x={badgeX} y={badgeY}>
        <Circle ref={badgeRef} radius={14} fill="white" stroke="#EBEDF3" strokeWidth={2} listening />
        {iconImageRef.current && (
          <Image
            image={iconImageRef.current}
            x={-7.75}
            y={-8}
            width={15}
            height={13}
            scaleX={1.1}
            scaleY={1.1}
            listening={false}
          />
        )}
        <KonvaTooltip text={badge.tooltip} nodeRef={badgeRef} side="bottom" offset={6} />
      </Group>
    </>
  );
}

// Keep legacy export for backward compatibility
export function MissingDataBadge(props: { x?: number; y?: number }) {
  return <DataLayerBadge errorType={DataLayerErrorType.MISSING_DATA} {...props} />;
}
